/**
 * Port of the app's `LogicEvaluator` (Android, `features/ruleconfig/domain/logic/LogicEvaluator.kt`):
 * the same tokenizer, grammar, value rules and error messages — keep in sync with the Kotlin file.
 *
 *   program   := statement*  |  expression          // bare expression == "return <expr>"
 *   statement := ifStmt | returnStmt
 *   ifStmt    := 'if' '(' expr ')' '{' returnStmt [';'] '}'
 *   returnStmt:= 'return' expr [';']
 *   expr      := or ; or := and ('||' and)* ; and := equality ('&&' equality)* ; …
 *   primary   := NUMBER | STRING | 'true' | 'false' | placeholder | '(' expr ')'
 *   placeholder := '{' IDENT (':' IDENT)? ('.' IDENT)* '}' ('.' IDENT)*
 *
 * `validateLogic` only parses (as `LogicEvaluator.validate`); `evaluateLogic` / `evaluateLogicOrZero`
 * also run the program against a fact map (as `evaluate` / `evaluateOrZero`), which is what the
 * console's calculation simulation uses.
 */

type TokType =
  | 'NUM'
  | 'STR'
  | 'IDENT'
  | 'LBRACE'
  | 'RBRACE'
  | 'LP'
  | 'RP'
  | 'OP'
  | 'DOT'
  | 'COLON'
  | 'SEMI'
  | 'EOF'

type Token = { type: TokType; text: string }

class LogicError extends Error {}

const EOF: Token = { type: 'EOF', text: '' }

const isLetter = (c: string) => /\p{L}/u.test(c)
const isDigit = (c: string) => /\p{Nd}/u.test(c)

function normalize(src: string): string {
  return src.replace(/[\u201C\u201D]/g, '"').replace(/[\u2018\u2019]/g, "'")
}

function tokenize(src: string): Token[] {
  const out: Token[] = []
  // Iterate by code point so a letter outside the BMP is one character, as in Kotlin's Char checks
  // for the common (BMP) case.
  const chars = Array.from(src)
  const n = chars.length
  const ch = (index: number): string => chars[index] ?? ''
  let i = 0
  while (i < n) {
    const c = ch(i)
    if (/\s/u.test(c)) {
      i++
    } else if (c === '{') {
      out.push({ type: 'LBRACE', text: '{' })
      i++
    } else if (c === '}') {
      out.push({ type: 'RBRACE', text: '}' })
      i++
    } else if (c === '(') {
      out.push({ type: 'LP', text: '(' })
      i++
    } else if (c === ')') {
      out.push({ type: 'RP', text: ')' })
      i++
    } else if (c === ';') {
      out.push({ type: 'SEMI', text: ';' })
      i++
    } else if (c === ':') {
      out.push({ type: 'COLON', text: ':' })
      i++
    } else if (c === '"' || c === "'") {
      const end = chars.indexOf(c, i + 1)
      if (end < 0) throw new LogicError('Unclosed string literal')
      out.push({ type: 'STR', text: chars.slice(i + 1, end).join('') })
      i = end + 1
    } else if (isDigit(c) || (c === '.' && isDigit(ch(i + 1)))) {
      const start = i
      i++
      while (i < n && (isDigit(ch(i)) || ch(i) === '.')) i++
      out.push({ type: 'NUM', text: chars.slice(start, i).join('') })
    } else if (c === '.') {
      out.push({ type: 'DOT', text: '.' })
      i++
    } else if (isLetter(c) || c === '_') {
      const start = i
      i++
      while (i < n && (isLetter(ch(i)) || isDigit(ch(i)) || ch(i) === '_')) i++
      out.push({ type: 'IDENT', text: chars.slice(start, i).join('') })
    } else {
      const two = i + 1 < n ? c + ch(i + 1) : ''
      if (['==', '!=', '<=', '>=', '&&', '||'].includes(two)) {
        out.push({ type: 'OP', text: two })
        i += 2
      } else if (['<', '>', '+', '-', '*', '/', '%', '!'].includes(c)) {
        out.push({ type: 'OP', text: c })
        i++
      } else {
        throw new LogicError(`Unexpected character '${c}'`)
      }
    }
  }
  out.push({ type: 'EOF', text: '' })
  return out
}

/**
 * Kotlin's `String.toDoubleOrNull` (the JVM `Double.valueOf` grammar without hex literals): optional
 * sign, `NaN` / `Infinity`, decimal digits with an optional exponent and an optional `f`/`d` suffix,
 * surrounded by optional control/space characters. `null` when the text is not a number.
 */
export function kotlinToDoubleOrNull(text: string): number | null {
  const match =
    // eslint-disable-next-line no-control-regex -- the JVM grammar allows \x00-\x20 padding
    /^[\x00-\x20]*([+-]?)(?:(NaN)|(Infinity)|((?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?)[fFdD]?)[\x00-\x20]*$/.exec(
      text,
    )
  if (!match) return null
  const [, sign = '', nan, infinity, decimal = ''] = match
  if (nan) return Number.NaN
  const value = infinity ? Number.POSITIVE_INFINITY : Number(decimal)
  return sign === '-' ? -value : value
}

// ---- AST ----

type V = { kind: 'num'; d: number } | { kind: 'str'; s: string } | { kind: 'bool'; b: boolean }

type Expr =
  | { kind: 'lit'; v: V }
  | { kind: 'placeholder'; key: string }
  | { kind: 'unary'; op: string; e: Expr }
  | { kind: 'binary'; op: string; l: Expr; r: Expr }

type Stmt = { kind: 'if'; cond: Expr; body: Expr } | { kind: 'return'; expr: Expr }

class Parser {
  private pos = 0
  private readonly tokens: Token[]

  constructor(tokens: Token[]) {
    this.tokens = tokens
  }

  private peek(): Token {
    return this.tokens[this.pos] ?? EOF
  }

  private next(): Token {
    return this.tokens[this.pos++] ?? EOF
  }

  private expect(type: TokType): Token {
    const t = this.peek()
    if (t.type !== type) throw new LogicError(`Expected ${type} but found '${t.text || 'end'}'`)
    return this.next()
  }

  private isKeyword(word: string): boolean {
    return this.peek().type === 'IDENT' && this.peek().text === word
  }

  private isOp(...ops: string[]): boolean {
    return this.peek().type === 'OP' && ops.includes(this.peek().text)
  }

  parseProgram(): Stmt[] {
    if (!this.isKeyword('if') && !this.isKeyword('return')) {
      if (this.peek().type === 'EOF') return []
      const expr = this.parseExpr()
      this.expect('EOF')
      return [{ kind: 'return', expr }]
    }
    const statements: Stmt[] = []
    while (this.peek().type !== 'EOF') {
      statements.push(this.parseStatement())
      while (this.peek().type === 'SEMI') this.next()
    }
    return statements
  }

  private parseStatement(): Stmt {
    if (this.isKeyword('if')) return this.parseIf()
    if (this.isKeyword('return')) return this.parseReturn()
    throw new LogicError(`Expected 'if' or 'return' but found '${this.peek().text || 'end'}'`)
  }

  private parseIf(): Stmt {
    this.next()
    this.expect('LP')
    const cond = this.parseExpr()
    this.expect('RP')
    this.expect('LBRACE')
    if (!this.isKeyword('return')) throw new LogicError("'if' block must contain a 'return'")
    const body = this.parseReturn()
    while (this.peek().type === 'SEMI') this.next()
    this.expect('RBRACE')
    return { kind: 'if', cond, body: body.expr }
  }

  private parseReturn(): { kind: 'return'; expr: Expr } {
    this.next()
    const expr = this.parseExpr()
    if (this.peek().type === 'SEMI') this.next()
    return { kind: 'return', expr }
  }

  private parseExpr(): Expr {
    return this.parseBinary(0)
  }

  /** Precedence levels, lowest first: || · && · == != · < > <= >= · + - · * / % (all left-assoc). */
  private static readonly LEVELS: string[][] = [
    ['||'],
    ['&&'],
    ['==', '!='],
    ['<', '>', '<=', '>='],
    ['+', '-'],
    ['*', '/', '%'],
  ]

  private parseBinary(level: number): Expr {
    if (level >= Parser.LEVELS.length) return this.parseUnary()
    let left = this.parseBinary(level + 1)
    while (this.isOp(...(Parser.LEVELS[level] ?? []))) {
      const op = this.next().text
      left = { kind: 'binary', op, l: left, r: this.parseBinary(level + 1) }
    }
    return left
  }

  private parseUnary(): Expr {
    if (this.isOp('!', '-')) {
      const op = this.next().text
      return { kind: 'unary', op, e: this.parseUnary() }
    }
    return this.parsePrimary()
  }

  private parsePrimary(): Expr {
    const t = this.peek()
    switch (t.type) {
      case 'NUM': {
        this.next()
        const d = kotlinToDoubleOrNull(t.text)
        if (d === null) throw new LogicError(`Invalid number '${t.text}'`)
        return { kind: 'lit', v: { kind: 'num', d } }
      }
      case 'STR':
        this.next()
        return { kind: 'lit', v: { kind: 'str', s: t.text } }
      case 'IDENT':
        if (t.text === 'true' || t.text === 'false') {
          this.next()
          return { kind: 'lit', v: { kind: 'bool', b: t.text === 'true' } }
        }
        throw new LogicError(`Unexpected identifier '${t.text}' (did you mean a {fact_key}?)`)
      case 'LBRACE':
        return this.parsePlaceholder()
      case 'LP': {
        this.next()
        const e = this.parseExpr()
        this.expect('RP')
        return e
      }
      default:
        throw new LogicError(`Unexpected '${t.text || 'end'}'`)
    }
  }

  private parsePlaceholder(): Expr {
    this.expect('LBRACE')
    let key = this.expect('IDENT').text
    // {attr:CODE}: looked up under the composite key "attr:CODE".
    if (this.peek().type === 'COLON') {
      this.next()
      key = `${key}:${this.expect('IDENT').text}`
    }
    while (this.peek().type === 'DOT') {
      this.next()
      this.expect('IDENT')
    }
    this.expect('RBRACE')
    while (this.peek().type === 'DOT') {
      this.next()
      this.expect('IDENT')
    }
    return { kind: 'placeholder', key }
  }
}

function parse(script: string): Stmt[] {
  return new Parser(tokenize(normalize(script))).parseProgram()
}

// ---- Evaluation (mirrors `execute` / `eval` / `evalBinary` of the Kotlin evaluator) ----

/** Fact map: fact key → stored answer (`attr:CODE` keys carry attribute values). */
export type Facts = Readonly<Record<string, string>>

function execute(program: Stmt[], facts: Facts): number {
  for (const statement of program) {
    if (statement.kind === 'if') {
      if (asBool(evaluate(statement.cond, facts))) return asNumber(evaluate(statement.body, facts))
    } else {
      return asNumber(evaluate(statement.expr, facts))
    }
  }
  return 0
}

function evaluate(node: Expr, facts: Facts): V {
  switch (node.kind) {
    case 'lit':
      return node.v
    case 'placeholder': {
      // Own properties only: a key like "constructor" must not reach Object.prototype.
      const raw = Object.prototype.hasOwnProperty.call(facts, node.key) ? facts[node.key] : undefined
      return resolve((raw ?? '').trim())
    }
    case 'unary':
      if (node.op === '-') return { kind: 'num', d: -asNumber(evaluate(node.e, facts)) }
      if (node.op === '!') return { kind: 'bool', b: !asBool(evaluate(node.e, facts)) }
      throw new LogicError(`Unknown operator '${node.op}'`)
    case 'binary':
      return evaluateBinary(node.op, node.l, node.r, facts)
  }
}

function evaluateBinary(op: string, leftNode: Expr, rightNode: Expr, facts: Facts): V {
  // Logical operators short-circuit.
  if (op === '&&') {
    return { kind: 'bool', b: asBool(evaluate(leftNode, facts)) && asBool(evaluate(rightNode, facts)) }
  }
  if (op === '||') {
    return { kind: 'bool', b: asBool(evaluate(leftNode, facts)) || asBool(evaluate(rightNode, facts)) }
  }
  const l = evaluate(leftNode, facts)
  const r = evaluate(rightNode, facts)
  const bool = (b: boolean): V => ({ kind: 'bool', b })
  const num = (d: number): V => ({ kind: 'num', d })
  switch (op) {
    case '==':
      return bool(valuesEqual(l, r))
    case '!=':
      return bool(!valuesEqual(l, r))
    case '<':
      return bool(asNumber(l) < asNumber(r))
    case '>':
      return bool(asNumber(l) > asNumber(r))
    case '<=':
      return bool(asNumber(l) <= asNumber(r))
    case '>=':
      return bool(asNumber(l) >= asNumber(r))
    case '+':
      return num(asNumber(l) + asNumber(r))
    case '-':
      return num(asNumber(l) - asNumber(r))
    case '*':
      return num(asNumber(l) * asNumber(r))
    case '/': {
      const d = asNumber(r)
      if (d === 0) throw new LogicError('Division by zero')
      return num(asNumber(l) / d)
    }
    case '%': {
      const d = asNumber(r)
      if (d === 0) throw new LogicError('Division by zero')
      return num(asNumber(l) % d)
    }
    default:
      throw new LogicError(`Unknown operator '${op}'`)
  }
}

function resolve(raw: string): V {
  const d = kotlinToDoubleOrNull(raw)
  if (d !== null) return { kind: 'num', d }
  if (raw.toLowerCase() === 'true') return { kind: 'bool', b: true }
  if (raw.toLowerCase() === 'false') return { kind: 'bool', b: false }
  return { kind: 'str', s: raw }
}

function asNumber(v: V): number {
  switch (v.kind) {
    case 'num':
      return v.d
    case 'bool':
      return v.b ? 1 : 0
    case 'str': {
      const d = kotlinToDoubleOrNull(v.s)
      if (d !== null) return d
      if (v.s === '') return 0
      throw new LogicError(`'${v.s}' is not a number`)
    }
  }
}

function asBool(v: V): boolean {
  switch (v.kind) {
    case 'bool':
      return v.b
    case 'num':
      return v.d !== 0
    case 'str':
      throw new LogicError('Expected a true/false condition')
  }
}

function valuesEqual(l: V, r: V): boolean {
  if (l.kind === 'num' && r.kind === 'num') return l.d === r.d
  if (l.kind === 'bool' && r.kind === 'bool') return l.b === r.b
  return stringOf(l).toLowerCase() === stringOf(r).toLowerCase()
}

/** Kotlin's rendering: whole numbers without ".0" (other doubles may differ from JVM in rare cases). */
function stringOf(v: V): string {
  switch (v.kind) {
    case 'num':
      return v.d % 1 === 0 ? String(Math.trunc(v.d)) : String(v.d)
    case 'bool':
      return String(v.b)
    case 'str':
      return v.s
  }
}

// ---- Public API ----

export type LogicResult = { ok: true; value: number } | { ok: false; error: string }

/** The app's parse error for `script`, or `null` when it is valid (`LogicEvaluator.validate`). */
export function validateLogic(script: string): string | null {
  try {
    parse(script)
    return null
  } catch (caught) {
    if (caught instanceof LogicError) return caught.message
    throw caught
  }
}

/** Evaluates `script` against `facts` (`LogicEvaluator.evaluate`). */
export function evaluateLogic(script: string, facts: Facts): LogicResult {
  try {
    return { ok: true, value: execute(parse(script), facts) }
  } catch (caught) {
    if (caught instanceof LogicError) return { ok: false, error: caught.message || 'Invalid expression' }
    throw caught
  }
}

/** `LogicEvaluator.evaluateOrZero`: 0 on any error (what the calculation engine uses). */
export function evaluateLogicOrZero(script: string, facts: Facts): number {
  const result = evaluateLogic(script, facts)
  return result.ok ? result.value : 0
}
